import type {
  AccountNature,
  SystemAccountCategory,
  SystemAccountModule,
} from '../generated/enums.js';

export interface SystemAccountSlotResponse {
  slotId: string;
  code: string;
  name: string;
  description?: string | null;
  module: SystemAccountModule;
  category: SystemAccountCategory;
  accountNature: AccountNature;
  isRequired: boolean;
  suggestedParentPattern?: string | null;
  suggestedNamePattern?: string | null;
  /** Accounting frameworks the slot exists under; empty means every framework. */
  applicableFrameworks?: string[];
  /** The framework the list was scoped to — name/description follow it; null when unscoped. */
  frameworkCode?: string | null;
  /**
   * Fiscal tax slot this HCM payable is an alias of, or null when it owns its own liability
   * (ALC-1545). Present for the four `HCM_PAYROLL_*_PAYABLE` tax slots only.
   */
  aliasedTaxSlotCode?: string | null;
  /**
   * True when a configured tax liability rail owns this slot's account, so it is system-managed
   * and must be presented read-only rather than as an independent settings choice.
   */
  isManagedByTaxRail?: boolean;
}

export interface SystemAccountMappingResponse {
  mappingId: string;
  tenantId: string;
  versionId: string;
  slotId: string;
  glAccountId: string | null;
  isActive: boolean;
  validFrom: string;
  validUntil?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

/**
 * Which CoA version a system-account mapping read or write addresses (ALC-1268 part 2).
 *
 * Omit everything only when the tenant has one unambiguous assigned scope. Pass
 * `entityId` (optionally narrowed by `bookId`) to address the version that scope actually POSTS
 * on, read from its active CoA-version assignment; that is the only way to configure slots for a
 * tenant whose entity/book assignment differs from the default scope. `versionId`
 * addresses a version directly and is accepted only when the tenant actually uses it.
 */
export interface SystemAccountMappingScopeQuery {
  versionId?: string;
  entityId?: string;
  bookId?: string;
}

export interface BulkUpsertSystemAccountMappingsRequest extends SystemAccountMappingScopeQuery {
  mappings: {
    slotId: string;
    glAccountId?: string | null;
  }[];
}

/**
 * Cash-layer offset-root slots an entity can switch to direct linking (ALC-4600): bank
 * accounts, investment bank accounts and credit cards.
 */
export const CASH_LAYER_SLOT_CODES = [
  'OP_BANK_ACCOUNT',
  'OP_INVESTMENT_SHORT_TERM',
  'OP_CREDIT_CARD',
] as const;

export type CashLayerSlotCode = (typeof CASH_LAYER_SLOT_CODES)[number];

/**
 * How an entity links one cash-layer slot's subjects to the chart. `ROOT_PROVISIONED` (the
 * default) creates a system-managed child under the slot's root on first posting;
 * `DIRECT_LINK` never creates one — each bank account or card is linked by hand to an
 * existing analytic account, and posting fails until it is.
 */
export type SystemAccountSlotTreatmentValue = 'ROOT_PROVISIONED' | 'DIRECT_LINK';

export interface EntitySystemAccountSlotTreatmentItem {
  slotId: string;
  slotCode: CashLayerSlotCode;
  treatment: SystemAccountSlotTreatmentValue;
  /** True when no row exists and the entity inherits ROOT_PROVISIONED. */
  isDefault: boolean;
  updatedAt: string | null;
}

export interface EntitySystemAccountSlotTreatmentListResponse {
  entityId: string;
  items: EntitySystemAccountSlotTreatmentItem[];
}

export interface UpdateEntitySystemAccountSlotTreatmentRequest {
  entityId: string;
  slotCode: CashLayerSlotCode;
  treatment: SystemAccountSlotTreatmentValue;
}

/**
 * Classification a system-account slot demands of the account mapped to it.
 *
 * `accountType` / `naturalBalance` use the GL enum spelling (`ASSET`, `DEBIT`), i.e. the
 * spelling `Account.accountType` / `Account.naturalBalance` carry on the wire and in the
 * database — not the lowercase web-form spelling.
 */
export interface SystemAccountRootSlotRule {
  accountType: 'ASSET' | 'LIABILITY';
  naturalBalance: 'DEBIT' | 'CREDIT';
}

/**
 * Slot categories whose mapped account is a NON-postable PARENT, keyed by
 * `SystemAccountSlot.category`.
 *
 * These are the GL "offset roots". The system creates one postable child per subject under
 * them — per contract (`AccountService.ensureContractManagedChildAccountId`), per vendor,
 * customer, bank account, investment position or card provider
 * (`OffsetAccountService.createNewOffsetAccount` / `ensureCreditCardProviderOffsetAccountId`,
 * always `isPostable: true` with `parentAccountId: <root>`). A postable root would therefore
 * be a postable parent holding postable system-managed children, which is not a valid
 * hierarchy: the root's own balance and its children's balances would double-count in every
 * roll-up. The product decision (ALC-2382) is that offset roots are non-postable parents.
 *
 * The classification is not decoration. The auto-created child inherits the root's
 * `accountType` / `naturalBalance` from the `OffsetAccountConfig` that owns the slot
 * (`apps/api/src/modules/gl/account/account.service.ts`), so a root on the wrong
 * balance-sheet side would silently park every future posting under the wrong hierarchy.
 *
 * This table is the SINGLE source of truth for both sides of the mapping screen:
 * `system-account-mapping.service.ts` validates writes against it, and
 * `apps/web/src/lib/system-account-slot-account-type.ts` builds the picker's options from it,
 * so the selector can never offer an account the API is about to reject.
 *
 * Keyed by category rather than by slot code on purpose: category is 1:1 with the offset-root
 * slot codes in the catalog, it is what the web actually groups rows by, and it lets the
 * integration fixtures exercise the rule with a randomised slot code.
 */
export const NON_POSTABLE_ROOT_SLOT_RULES = {
  // Billing contract roots (ALC-1251) — one postable child per contract.
  CONTRACT_ASSET: { accountType: 'ASSET', naturalBalance: 'DEBIT' },
  CONTRACT_LIABILITY: { accountType: 'LIABILITY', naturalBalance: 'CREDIT' },
  // AP / AR / treasury offset roots (ALC-1997 / ALC-2001) — one postable child per subject.
  VENDOR_LIABILITY: { accountType: 'LIABILITY', naturalBalance: 'CREDIT' },
  SUPPLIER_ADVANCE: { accountType: 'ASSET', naturalBalance: 'DEBIT' },
  CUSTOMER_RECEIVABLE: { accountType: 'ASSET', naturalBalance: 'DEBIT' },
  BANK_ACCOUNT: { accountType: 'ASSET', naturalBalance: 'DEBIT' },
  INVESTMENT_SHORT_TERM: { accountType: 'ASSET', naturalBalance: 'DEBIT' },
  INVESTMENT_LONG_TERM: { accountType: 'ASSET', naturalBalance: 'DEBIT' },
  IN_TRANSIT: { accountType: 'ASSET', naturalBalance: 'DEBIT' },
  CREDIT_CARD_LIABILITY: { accountType: 'LIABILITY', naturalBalance: 'CREDIT' },
} as const satisfies Partial<Record<SystemAccountCategory, SystemAccountRootSlotRule>>;

export type NonPostableRootSlotCategory = keyof typeof NON_POSTABLE_ROOT_SLOT_RULES;

/** Every category that must be mapped to a non-postable parent. */
export const NON_POSTABLE_ROOT_SLOT_CATEGORIES = Object.keys(
  NON_POSTABLE_ROOT_SLOT_RULES,
) as NonPostableRootSlotCategory[];

/**
 * The rule for `category`, or `null` when the category's slot posts directly and therefore
 * requires a postable leaf. Accepts a plain string so callers can pass a category that a
 * newer API version added without the build breaking.
 */
export function nonPostableRootSlotRule(category: string): SystemAccountRootSlotRule | null {
  return (
    (NON_POSTABLE_ROOT_SLOT_RULES as Record<string, SystemAccountRootSlotRule | undefined>)[
      category
    ] ?? null
  );
}

/** True when this category's slot maps a non-postable parent instead of a postable leaf. */
export function systemSlotRequiresNonPostableParent(category: string): boolean {
  return nonPostableRootSlotRule(category) !== null;
}

/**
 * Stable error codes the bulk system-account-mapping write returns. Clients classify on these,
 * never on the message text.
 */
export const SystemAccountMappingErrorCode = {
  ACCOUNT_NOT_FOUND: 'SYSTEM_ACCOUNT_MAPPING_ACCOUNT_NOT_FOUND',
  ACCOUNT_WRONG_VERSION: 'SYSTEM_ACCOUNT_MAPPING_ACCOUNT_WRONG_VERSION',
  ACCOUNT_MUST_BE_POSTABLE: 'SYSTEM_ACCOUNT_MAPPING_ACCOUNT_MUST_BE_POSTABLE',
  ROOT_MUST_BE_NON_POSTABLE: 'SYSTEM_ACCOUNT_MAPPING_ROOT_MUST_BE_NON_POSTABLE',
  ROOT_CLASSIFICATION_MISMATCH: 'SYSTEM_ACCOUNT_MAPPING_ROOT_CLASSIFICATION_MISMATCH',
  ROOT_SHARED_BY_SLOTS: 'SYSTEM_ACCOUNT_MAPPING_ROOT_SHARED_BY_SLOTS',
} as const;
export type SystemAccountMappingErrorCode =
  (typeof SystemAccountMappingErrorCode)[keyof typeof SystemAccountMappingErrorCode];

/**
 * Which frameworks a system account slot exists under, and how a framework words it.
 *
 * Both decisions are shared contracts, not per-module conventions: a slot listing that skips the
 * framework filter offers a Mexican slot to a Brazilian chart of accounts, and one that skips the
 * label shows the generic Portuguese wording on a Mexican one. `apps/api` (the mapping settings
 * screen) and `packages/services` (the ledger view preset options) both route through these, so
 * there is exactly one answer per question.
 */

/** A slot's per-framework wording, as the seeder records it under `frameworkLabels`. */
export interface SystemAccountSlotFrameworkLabel {
  name?: string;
  description?: string;
}

/**
 * True when a slot belongs in a chart on `frameworkCode`.
 *
 * An empty `applicableFrameworks` means "every framework" — that is the common case and must stay
 * cheap. A null/undefined `frameworkCode` means the caller is unscoped (integrations and scripts
 * resolving by code), and then every slot applies.
 */
export function isSlotApplicableToFramework(
  applicableFrameworks: readonly string[] | null | undefined,
  frameworkCode: string | null | undefined,
): boolean {
  if (!frameworkCode) return true;
  if (!applicableFrameworks || applicableFrameworks.length === 0) return true;
  return applicableFrameworks.includes(frameworkCode);
}

/** The wording a framework gives a slot, when the seed recorded one. */
export function systemAccountSlotFrameworkLabel(
  frameworkLabels: unknown,
  frameworkCode: string | null | undefined,
): SystemAccountSlotFrameworkLabel | null {
  if (!frameworkCode) return null;
  if (!frameworkLabels || typeof frameworkLabels !== 'object' || Array.isArray(frameworkLabels)) {
    return null;
  }
  const entry = (frameworkLabels as Record<string, unknown>)[frameworkCode];
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return null;
  const { name, description } = entry as { name?: unknown; description?: unknown };
  return {
    ...(typeof name === 'string' ? { name } : {}),
    ...(typeof description === 'string' ? { description } : {}),
  };
}
